/**
 * Centralized Logger Service
 *
 * Provides consistent, controllable logging across the application.
 * Controlled by DEBUG_CONFIG in src/config/debug.config.ts
 *
 * Usage:
 *   import { logger } from '@/services/loggerService';
 *
 *   logger.debug('auth', 'Token refreshed', { userId });
 *   logger.info('api', 'Request completed', { endpoint, status: 200 });
 *   logger.warn('media', 'Falling back to VP8 codec');
 *
 * For errors, continue using errorReporter directly:
 *   import { errorReporter } from '@/services/errorReportingService';
 *   errorReporter.critical('Upload failed', error, { feature: 'upload' });
 */

import { DEBUG_CONFIG, LOG_LEVEL_PRIORITY, type LogFeature, type LogLevel } from '@/config/debug.config';

/**
 * Check if a log should be shown based on current config
 */
const shouldLog = (level: LogLevel, feature: LogFeature): boolean => {
  // Always skip if disabled (except errors which use errorReporter)
  if (!DEBUG_CONFIG.ENABLED) {
    return false;
  }

  // Check level threshold
  const configLevelPriority = LOG_LEVEL_PRIORITY[DEBUG_CONFIG.LEVEL];
  const logLevelPriority = LOG_LEVEL_PRIORITY[level];
  if (logLevelPriority < configLevelPriority) {
    return false;
  }

  // Check feature filter (empty = all features allowed)
  if (DEBUG_CONFIG.FEATURES.length > 0 && !DEBUG_CONFIG.FEATURES.includes(feature)) {
    return false;
  }

  return true;
};

/**
 * Format log message with level and feature prefix
 */
const formatMessage = (level: LogLevel, feature: LogFeature, message: string): string => {
  return `[website][${level}][${feature}] ${message}`;
};

/**
 * Get console color style for each level
 */
const getLevelStyle = (level: LogLevel): string => {
  switch (level) {
    case 'DEBUG':
      return 'color: #888; font-weight: normal';
    case 'INFO':
      return 'color: #0066cc; font-weight: normal';
    case 'WARN':
      return 'color: #cc6600; font-weight: bold';
    case 'ERROR':
      return 'color: #cc0000; font-weight: bold';
  }
};

/**
 * Logger object with level-specific methods
 */
export const logger = {
  /**
   * Debug level - detailed debugging information
   * Only shown when DEBUG_CONFIG.ENABLED = true and LEVEL <= DEBUG
   */
  debug: (feature: LogFeature, message: string, data?: unknown): void => {
    if (!shouldLog('DEBUG', feature)) return;

    const formatted = formatMessage('DEBUG', feature, message);
    if (data !== undefined) {
      console.log(`%c${formatted}`, getLevelStyle('DEBUG'), data);
    } else {
      console.log(`%c${formatted}`, getLevelStyle('DEBUG'));
    }
  },

  /**
   * Info level - general information
   * Only shown when DEBUG_CONFIG.ENABLED = true and LEVEL <= INFO
   */
  info: (feature: LogFeature, message: string, data?: unknown): void => {
    if (!shouldLog('INFO', feature)) return;

    const formatted = formatMessage('INFO', feature, message);
    if (data !== undefined) {
      console.info(`%c${formatted}`, getLevelStyle('INFO'), data);
    } else {
      console.info(`%c${formatted}`, getLevelStyle('INFO'));
    }
  },

  /**
   * Warn level - potential issues or important notices
   * Only shown when DEBUG_CONFIG.ENABLED = true and LEVEL <= WARN
   */
  warn: (feature: LogFeature, message: string, data?: unknown): void => {
    if (!shouldLog('WARN', feature)) return;

    const formatted = formatMessage('WARN', feature, message);
    if (data !== undefined) {
      console.warn(`%c${formatted}`, getLevelStyle('WARN'), data);
    } else {
      console.warn(`%c${formatted}`, getLevelStyle('WARN'));
    }
  },

  /**
   * Check if debug logging is currently enabled
   * Useful for expensive debug operations
   */
  isEnabled: (): boolean => {
    return DEBUG_CONFIG.ENABLED;
  },

  /**
   * Check if a specific feature is being logged
   */
  isFeatureEnabled: (feature: LogFeature): boolean => {
    if (!DEBUG_CONFIG.ENABLED) return false;
    if (DEBUG_CONFIG.FEATURES.length === 0) return true;
    return DEBUG_CONFIG.FEATURES.includes(feature);
  },
};

export default logger;
